<template>
    <header class="sticky top-0 z-10 border-b border-default bg-default/95 backdrop-blur">
        <div class="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
            <!--
                The house's mark: its LOGOTYPE where it has uploaded one, and its
                monogram where it has not.

                The monogram is not going away and is not a lesser option — it is what
                a house that has never uploaded a file still has, and it is always
                there. But a guest booking on a hotel's own site should be looking at
                the hotel, and the picture was already in the store: the offer page and
                the invoice have printed it for a while.

                The square is painted in the house orange exactly — brand-400 — and not
                in the semantic primary. Everywhere else the primary is the darkened
                step of the same ramp, because white text on the house orange cannot be
                read; the mark is identity, and identity may not be a near miss. It
                carries near-black initials rather than white, which is both how the
                colour becomes legible and how the Imprion mark is drawn. See
                assets/main.css for why the ramp is built the way it is.
            -->
            <img
                v-if="logo"
                :src="logo"
                :alt="title"
                class="size-10 shrink-0 rounded-xl object-contain"
            >
            <div
                v-else
                class="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-400 text-lg font-extrabold text-brand-950"
            >
                {{ monogram }}
            </div>

            <div class="min-w-0 flex-1">
                <p class="truncate font-semibold text-highlighted">{{ title }}</p>
                <p v-if="tagline" class="truncate text-xs tracking-widest text-muted">{{ tagline }}</p>
            </div>

            <USelect
                v-model="locale"
                :items="languages"
                size="sm"
                :aria-label="$t('general.language')"
                class="w-32 shrink-0"
            />

            <UButton
                :icon="themeIcon"
                color="neutral"
                variant="ghost"
                :aria-label="$t('general.theme')"
                @click="cycleTheme"
            />
        </div>
    </header>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { useBookingStore } from '@/stores/booking'

    const store = useBookingStore()
    const { locale: i18nLocale } = useI18n()

    const title = computed((): string =>
        store.property?.appearance?.display_name || store.property?.name || '')

    const tagline = computed((): string => store.property?.appearance?.tagline ?? '')

    /**
     * The house's logotype, from the public photo path — no session, because there is
     * nobody signed in on a booking site. Empty where the house has uploaded none, and
     * the monogram below stands in.
     */
    const logo = computed((): string => {
        const key = store.property?.appearance?.logo_key

        return key ? `/api/pms/v1/photos/${key}` : ''
    })

    const monogram = computed((): string => {
        const set = store.property?.appearance?.monogram
        if (set) return set

        return (store.property?.name ?? '?').trim().slice(0, 2).toUpperCase()
    })

    /*
     * The six languages the guest surfaces speak, each written in ITSELF — a guest
     * finds their own without first having to read ours. The same list, in the same
     * order, as the room screen and the guest's phone (pms-display/stores/locale.ts).
     */
    const languages = [
        { label: 'Slovenščina', value: 'sl' },
        { label: 'English',     value: 'en' },
        { label: 'Deutsch',     value: 'de' },
        { label: 'Italiano',    value: 'it' },
        { label: 'Hrvatski',    value: 'hr' },
        { label: 'Magyar',      value: 'hu' },
    ]

    // The chosen language is remembered because it is not only what the screen is
    // drawn in — it is what the confirmation letter is written in.
    const locale = computed({
        get: (): string => i18nLocale.value,
        set: (value: string) => {
            i18nLocale.value = value
            localStorage.setItem('locale', value)
        },
    })

    // System / Light / Dark, in that order, exactly as everywhere else in the house.
    const themeIcon = computed((): string => ({
        auto:  'i-lucide-monitor',
        light: 'i-lucide-sun',
        dark:  'i-lucide-moon',
    }[store.colorMode as string] ?? 'i-lucide-monitor'))

    const cycleTheme = () => {
        const order = ['auto', 'light', 'dark'] as const
        const next = order[(order.indexOf(store.colorMode as typeof order[number]) + 1) % order.length]

        store.colorMode = next
    }
</script>
